import { ChangeEvent, FormEvent, useState } from 'react';
import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormElements } from 'web-components';

interface EditEquipmentGroupModalProps {
  groups: string[];
  isSaving: boolean;
  onClose: () => void;
  onSave: (groups: string[]) => Promise<void>;
}

const labelAmend = 'machines.machine_details.equipment_group';

const EditEquipmentGroupModal = ({ groups, isSaving, onClose, onSave }: EditEquipmentGroupModalProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const group = value.trim();
  const error = group.includes(',') ? 'invalid_group' : groups.includes(group) ? 'duplicate_group' : '';
  const canSave = Boolean(group) && !error && !isSaving;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (canSave) void onSave([...groups, group]);
  };

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        if (!isSaving && reason !== 'backdropClick') onClose();
      }}
      aria-labelledby="equipment-group-dialog-title"
      PaperProps={{ sx: { width: 464, maxWidth: 'calc(100% - 32px)', borderRadius: 1 } }}
    >
      <form onSubmit={submit}>
        <DialogTitle
          id="equipment-group-dialog-title"
          sx={{ borderTop: '6px solid', borderColor: 'primary.main', pt: 2, pr: 6, pb: 0.5 }}
        >
          {t(`${labelAmend}.edit_modal.title`)}
          <IconButton
            aria-label={t(`${labelAmend}.close`)}
            data-test-id="equipment-group-close"
            disabled={isSaving}
            onClick={onClose}
            sx={{ position: 'absolute', right: 12, top: 14 }}
            size="small"
            color="primary"
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{t(`${labelAmend}.edit_modal.subtitle`)}</DialogContentText>
          <FormElements.TextField
            size="medium"
            type="text"
            sx={{}}
            bgcolor={undefined}
            autoFocus
            fullWidth
            id="equipment-group-name"
            inputProps={{ 'data-test-id': 'equipment-group-name' }}
            name="equipment_group"
            label={t(`${labelAmend}.edit_modal.form.equipment_group`)}
            value={value}
            disabled={isSaving}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setValue(event.target.value)}
            error={Boolean(error)}
            helperText={
              error ? t(`${labelAmend}.${error}`) : t(`${labelAmend}.edit_modal.form.equipment_group_helper_text`)
            }
          />
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, justifyContent: 'space-between', bgcolor: '#f7f7fa', borderTop: '1px solid #b9b9bc' }}
        >
          <Button color="error" data-test-id="equipment-group-cancel" disabled={isSaving} onClick={onClose}>
            {t('default_actions.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            data-test-id="equipment-group-save"
            disabled={!canSave}
            sx={{ px: 5, py: 1.5 }}
          >
            {t('default_actions.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditEquipmentGroupModal;
